/**
 * @file ProductsToolbar.tsx
 *
 * The products list controls row: the grid/list view toggle, the stock and
 * category filter dropdowns, and the search field. Presentational — option data,
 * the current selections, and all change handlers are supplied by the parent
 * screen, which owns fetching and filter state.
 */

import { Icon } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { GridListToggle } from './GridListToggle';
import { ProductFilterDropdown, type FilterOption } from './ProductFilterDropdown';
import type { ViewMode } from './product-list';
import styles from './ProductsToolbar.module.css';

interface ProductsToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (viewMode: ViewMode) => void;
  stockOptions: FilterOption[];
  stockSelected: string[];
  onStockApply: (values: string[]) => void;
  statusOptions: FilterOption[];
  statusSelected: string[];
  onStatusApply: (values: string[]) => void;
  categoryOptions: FilterOption[];
  categorySelected: string[];
  onCategoryApply: (values: string[]) => void;
  search: string;
  onSearchChange: (value: string) => void;
}

/**
 * Renders the products toolbar (view toggle, filters, search).
 *
 * @param props - View mode, filter options/selections, search value, and handlers.
 *
 * @returns The toolbar element.
 */
export function ProductsToolbar({
  viewMode,
  onViewModeChange,
  stockOptions,
  stockSelected,
  onStockApply,
  statusOptions,
  statusSelected,
  onStatusApply,
  categoryOptions,
  categorySelected,
  onCategoryApply,
  search,
  onSearchChange,
}: ProductsToolbarProps) {
  const intl = useIntl();

  return (
    <div className={styles.productsMenu}>
      <div className={styles.productsMenuLeft}>
        <div className="gv-max-tab-sm-hidden">
          <GridListToggle viewMode={viewMode} setViewMode={onViewModeChange} />
        </div>
        <ProductFilterDropdown
          testId="products-filter-stock"
          label={intl.formatMessage(adminMessages.productsFilterStock)}
          resetLabel={intl.formatMessage(adminMessages.productsFilterResetStock)}
          options={stockOptions}
          selected={stockSelected}
          onApply={onStockApply}
        />
        <ProductFilterDropdown
          testId="products-filter-status"
          label={intl.formatMessage(adminMessages.productsFilterStatus)}
          resetLabel={intl.formatMessage(adminMessages.productsFilterResetStatus)}
          options={statusOptions}
          selected={statusSelected}
          onApply={onStatusApply}
        />
        <ProductFilterDropdown
          testId="products-filter-category"
          label={intl.formatMessage(adminMessages.productsFilterCategory)}
          resetLabel={intl.formatMessage(adminMessages.productsFilterResetCategory)}
          options={categoryOptions}
          selected={categorySelected}
          onApply={onCategoryApply}
        />
      </div>
      <div className="gv-mode-condensed">
        <div className="gv-input gv-input-search gv-surface-bright">
          <input
            type="search"
            data-testid="products-search"
            placeholder={intl.formatMessage(adminMessages.productsSearchPlaceholder)}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label={intl.formatMessage(adminMessages.productsSearchLabel)}
          />
          <span className="gv-addon" aria-hidden="true">
            <Icon name="search" size="small" />
          </span>
        </div>
      </div>
    </div>
  );
}
